/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import * as monaco from 'monaco-editor';

import { FunctionMetadata, ParsedFunctionCall } from '../types/LiveAgentEditorTypes';

import { AstParser } from './AstParser';
import { MetadataManager } from './MetadataManager';
import { VariableHelper } from './VariableHelper';

import { Step as UIStep } from '@/pages/projects/LiveAgentTypes';

/**
 * Utility class for converting between UI steps and code representations.
 * Handles the transformation of browser automation steps between their visual representation
 * in the UI and their corresponding TypeScript code implementation.
 */
export class StepConverter {
    /**
     * Converts an array of UI steps into TypeScript code.
     *
     * @param inputSteps - The array of UI steps to convert to code
     * @returns The generated TypeScript code as a string
     */
    public static stepsToCode(inputSteps: UIStep[]): string {
        const lines: string[] = [];

        // For each step, decide on method + args, then add it as a statement
        for (let i = 0; i < inputSteps.length; ++i) {
            const step = inputSteps[i];

            // If we already have code, just add it
            if (step.code) {
                lines.push(step.code);
                continue;
            }

            let method: string;
            let args: Array<any> = [];

            switch (step.kind) {
                case 'gotoUrl':
                    method = 'gotoUrl';
                    args = [step.url ?? ''];
                    break;
                case 'wait':
                    method = 'wait';
                    args = [step.durationMs ?? 0];
                    break;
                case 'pressKey':
                    method = 'pressKey';
                    args = [step.key ?? ''];
                    break;
                case 'aiAtomic':
                    switch (step.action) {
                        case 'leftClick':
                            method = 'leftClick';
                            args = [step.target ?? ''];
                            break;
                        case 'typeText':
                            method = 'typeText';
                            args = [step.target ?? '', step.value ?? ''];
                            break;
                        case 'selectOption':
                            method = 'selectOption';
                            args = [step.target ?? '', step.value ?? ''];
                            break;
                        case 'hover':
                            method = 'hover';
                            args = [step.target ?? ''];
                            break;
                        case 'extractSelector':
                            method = 'extractSelector';
                            args = [step.target ?? ''];
                            break;
                        case 'pressEnter':
                            method = 'pressEnter';
                            args = [step.target ?? ''];
                            break;
                        default:
                            throw new Error(`Unknown aiAtomic action: ${step.action}`);
                    }

                    // Add options if needed
                    if (
                        (step.targetingStrategy && step.targetingStrategy !== 'ai') ||
                        step.intent ||
                        step.mediaAssets
                    ) {
                        const options = {
                            ...(step.targetingStrategy && { targetingStrategy: step.targetingStrategy }),
                            ...(step.intent && { intent: step.intent }),
                            ...(step.mediaAssets && { mediaAssets: step.mediaAssets }),
                        };

                        args.push(options);
                    }
                    break;
                case 'aiExtractInfo':
                    method = 'aiExtractInfo';
                    args = [step.description ?? ''];
                    break;
                case 'emailLogin':
                    method = 'emailLogin';
                    args = [step.address ?? ''];
                    break;
                case 'emailSignup':
                    method = 'emailSignup';
                    args = [step.signupMethod ?? '', step.intent ?? ''];
                    if (step.address) {
                        args.push(step.address);
                    }
                    break;
                case 'readSMS':
                    method = 'readSMS';
                    args = [step.phoneNumber ?? ''];
                    break;
                case 'execPlaywright':
                    // For execPlaywright, we want to embed the JavaScript code directly
                    lines.push(`${step.pwCode ?? ''}\n`);

                    // Add a separator comment if the next step is also execPlaywright
                    if (i + 1 < inputSteps.length && inputSteps[i + 1].kind === 'execPlaywright') {
                        lines.push(`// STEP_SEPARATOR\n`);
                    }
                    continue;
                case 'simpleAssertion':
                    method = 'simpleAssertion';
                    args = [step.input ?? '', step.value ?? '', step.operator ?? ''];
                    break;
                case 'aiAssertion':
                    method = 'aiAssertion';
                    args = [step.assertion ?? ''];
                    break;
                default:
                    throw new Error(`Unknown step kind: ${step.kind}`);
            }

            // Format the arguments as a string
            const formattedArgs = args.map((arg) => this.stringifyArgument(arg)).join(', ');

            // Construct the complete statement
            let statement = '';

            // For functions that now return a value directly instead of taking a variable name
            if (
                (method === 'extractSelector' && step.variableName) ||
                (method === 'aiExtractInfo' && step.variableName) ||
                (method === 'readSMS' && step.toVariableName) ||
                (method === 'emailSignup' && step.saveCodeToVariable)
            ) {
                const variableName =
                    method === 'extractSelector'
                        ? step.variableName
                        : method === 'aiExtractInfo'
                          ? step.variableName
                          : method === 'readSMS'
                            ? step.toVariableName
                            : step.saveCodeToVariable;

                statement = `const ${variableName} = await Heal.${method}(${formattedArgs});\n`;
            } else {
                statement = `await Heal.${method}(${formattedArgs});\n`;
            }

            lines.push(statement);
        }

        // Join the lines with newlines
        return lines.join('\n');
    }

    /**
     * Converts a JavaScript value to its string representation
     * @param value - The value to stringify
     * @returns The string representation of the value
     */
    private static stringifyArgument(value: any): string {
        if (typeof value === 'string') {
            // Escape any single quotes in the string and wrap in single quotes
            return VariableHelper.healVariableToCode(value.replace(/'/g, "\\'"));
        } else if (value === null) {
            return 'null';
        } else if (Array.isArray(value)) {
            const items = value.map((item) => this.stringifyArgument(item)).join(', ');
            return `[${items}]`;
        } else if (typeof value === 'object') {
            const entries = Object.entries(value)
                .map(([key, val]) => `${key}: ${this.stringifyArgument(val)}`)
                .join(', ');
            return `{${entries}}`;
        }
        return String(value);
    }

    /**
     * Converts TypeScript code from the editor back into UI steps.
     *
     * @param editorCode - The TypeScript code from the editor
     * @param editorRef - Reference to the Monaco editor instance
     * @param astParser - The AST parser used to analyze the code
     * @param metadataManager - Manager for accessing function metadata
     * @returns An array of UIStep objects representing the steps in the code
     */
    public static codeToSteps(
        editorCode: string,
        editorRef: monaco.editor.IStandaloneCodeEditor,
        astParser: AstParser,
        metadataManager: MetadataManager,
    ): UIStep[] {
        const functionCalls = astParser.parseCode(editorCode);
        const model = editorRef?.getModel();
        if (!model) return [];

        return functionCalls
            .map((call: ParsedFunctionCall) => {
                // Get decorations for this function call
                const decorations = model.getDecorationsInRange({
                    startLineNumber: call.functionPosition.lineNumberStart,
                    startColumn: call.functionPosition.columnStart,
                    endLineNumber: call.functionPosition.lineNumberEnd,
                    endColumn: call.functionPosition.columnEnd,
                });

                const decorationOptions = decorations?.find((d: monaco.editor.IModelDecoration) => {
                    const options = d.options as any;
                    return options.description?.healKey;
                })?.options as any | undefined;

                const key = decorationOptions?.description?.healKey;

                // Get the code with its formatting, comments, and empty lines using the decorationsRange
                const code = model.getValueInRange({
                    startLineNumber: call.decorationsPosition.lineNumberStart,
                    startColumn: call.decorationsPosition.columnStart,
                    endLineNumber: call.decorationsPosition.lineNumberEnd,
                    endColumn: call.decorationsPosition.columnEnd,
                });

                if (!key) {
                    throw new Error(`No key found for function call: ${call.methodName}`);
                }

                // Get the associated metadata if any
                const metadata = metadataManager.getMetadataById(key);
                if (!metadata) {
                    throw new Error(`No metadata found for key: ${key}`);
                }

                return StepConverter.metadataToStep(metadata, code);
            })
            .filter(Boolean) as UIStep[];
    }

    /**
     * Converts function metadata back to a UI step.
     *
     * @param metadata - The metadata object containing function call information
     * @param code - The original code string for this step
     * @returns A UIStep object representing the step
     */
    public static metadataToStep(metadata: FunctionMetadata, code: string): UIStep | null {
        const { methodName, args } = metadata.call;

        // Common properties for all steps
        const baseStep = {
            id: metadata.key,
            executed: metadata.executed || false,
            running: metadata.running || false,
            result: metadata.result || 'UNKNOWN',
            ...(metadata.actions && { actions: metadata.actions }),
            ...(metadata.childActions && { childActions: metadata.childActions }),
            code,
        };

        // For the functions we modified, the variable name is stored in the metadata
        // under the variableName property (added by AstParser)
        const variableName = metadata.call.variableName || '';

        if (methodName === 'execPlaywright') {
            // For execPlaywright, we want to embed the JavaScript code directly
            // We remove lines until we find the // STEP_SEPARATOR line, and we remove it too
            // If there is no // STEP_SEPARATOR line, we put the whole code
            const lines = code.split('\n');
            let index = lines.findIndex((line) => line.trim() === '// STEP_SEPARATOR');
            if (index === -1) {
                index = lines.length;
            }
            const newCode = lines.slice(0, index).join('\n');
            return {
                kind: 'execPlaywright',
                pwCode: newCode,
                ...baseStep,
            };
        }

        switch (methodName) {
            case 'gotoUrl':
                return {
                    kind: 'gotoUrl',
                    url: VariableHelper.healCodeToVariable(args[0]),
                    ...baseStep,
                };
            case 'wait':
                return {
                    kind: 'wait',
                    durationMs: VariableHelper.healCodeToVariable(args[0]),
                    ...baseStep,
                };
            case 'pressKey':
                return {
                    kind: 'pressKey',
                    key: VariableHelper.healCodeToVariable(args[0]),
                    ...baseStep,
                };
            case 'leftClick':
                return {
                    kind: 'aiAtomic',
                    action: 'leftClick',
                    target: VariableHelper.healCodeToVariable(args[0]),
                    ...(args[1] && typeof args[1].value === 'object' && { ...args[1].value }),
                    ...baseStep,
                };
            case 'typeText':
                return {
                    kind: 'aiAtomic',
                    action: 'typeText',
                    target: VariableHelper.healCodeToVariable(args[0]),
                    value: VariableHelper.healCodeToVariable(args[1]),
                    ...(args[2] && typeof args[2].value === 'object' && { ...args[2].value }),
                    ...baseStep,
                };
            case 'selectOption':
                return {
                    kind: 'aiAtomic',
                    action: 'selectOption',
                    target: VariableHelper.healCodeToVariable(args[0]),
                    value: VariableHelper.healCodeToVariable(args[1]),
                    ...(args[2] && typeof args[2].value === 'object' && { ...args[2].value }),
                    ...baseStep,
                };
            case 'hover':
                return {
                    kind: 'aiAtomic',
                    action: 'hover',
                    target: VariableHelper.healCodeToVariable(args[0]),
                    ...(args[1] && typeof args[1].value === 'object' && { ...args[1].value }),
                    ...baseStep,
                };
            case 'extractSelector':
                return {
                    kind: 'aiAtomic',
                    action: 'extractSelector',
                    target: VariableHelper.healCodeToVariable(args[0]),
                    variableName,
                    ...(args[1] && typeof args[1].value === 'object' && { ...args[1].value }),
                    ...baseStep,
                };
            case 'pressEnter':
                return {
                    kind: 'aiAtomic',
                    action: 'pressEnter',
                    target: VariableHelper.healCodeToVariable(args[0]),
                    ...(args[1] && typeof args[1].value === 'object' && { ...args[1].value }),
                    ...baseStep,
                };
            case 'aiExtractInfo':
                return {
                    kind: 'aiExtractInfo',
                    description: VariableHelper.healCodeToVariable(args[0]),
                    variableName,
                    ...baseStep,
                };
            case 'emailLogin':
                return {
                    kind: 'emailLogin',
                    address: VariableHelper.healCodeToVariable(args[0]),
                    ...baseStep,
                };
            case 'emailSignup':
                return {
                    kind: 'emailSignup',
                    signupMethod: VariableHelper.healCodeToVariable(args[0]),
                    intent: VariableHelper.healCodeToVariable(args[1]),
                    address: args[2] ? VariableHelper.healCodeToVariable(args[2]) : undefined,
                    saveCodeToVariable: variableName,
                    ...baseStep,
                };
            case 'readSMS':
                return {
                    kind: 'readSMS',
                    phoneNumber: VariableHelper.healCodeToVariable(args[0]),
                    toVariableName: variableName,
                    ...baseStep,
                };
            case 'simpleAssertion':
                return {
                    kind: 'simpleAssertion',
                    input: VariableHelper.healCodeToVariable(args[0]),
                    value: VariableHelper.healCodeToVariable(args[1]),
                    operator: args[2].value as string,
                    ...baseStep,
                };
            case 'aiAssertion':
                return {
                    kind: 'aiAssertion',
                    assertion: VariableHelper.healCodeToVariable(args[0]),
                    ...baseStep,
                };
            default:
                return null;
        }
    }

    /**
     * Unescapes a string by replacing escaped characters with their actual values.
     * @param str - The string to unescape
     * @returns The unescaped string
     */
    static unescapeString(str: string): string {
        return str
            .replace(/\\(['"])/g, '$1')
            .replace(/\\n/g, '\n')
            .replace(/\\r/g, '\r')
            .replace(/\\t/g, '\t');
    }

    private constructor() {}
}
